import type { AuthAdapter } from './AuthAdapter';
import type { StoredSession, User } from './types';

// Epoch-ms timestamps stored after every successful token issue or refresh.
// Used by useProactiveTokenRefresh to determine whether the token is old enough
// to warrant a refresh on tab focus, without waiting for a 401.
const TOKEN_EXPIRES_AT_KEY = 'mdvip_token_expires_at';
const TOKEN_ISSUED_AT_KEY = 'mdvip_token_issued_at';

// Separate from the idle-sync channel so logout propagation is independent of
// activity sync. useIdleTimeout manages its own 'mdvip-idle-sync' channel for
// mousemove/keydown throttling; this channel is only for auth events.
const AUTH_CHANNEL = 'mdvip-auth';

interface WebAuthAdapterConfig {
  baseUrl: string;
}

/**
 * Browser-side auth adapter for SAML SSO with httpOnly cookie transport.
 *
 * Tokens are stored exclusively in httpOnly cookies set by the API — never in
 * localStorage or memory. This means JS has no direct token access; the browser
 * attaches them automatically via `credentials: 'include'` on every request.
 */
export class WebAuthAdapter implements AuthAdapter {
  private readonly baseUrl: string;

  constructor({ baseUrl }: WebAuthAdapterConfig) {
    this.baseUrl = baseUrl;
  }

  async initiateLogin(): Promise<void> {
    // `transport=cookie` tells the API to issue httpOnly cookies instead of
    // returning tokens in the response body. The callbackUrl is where the API
    // redirects after SAML assertion validation, passing a short-lived code.
    const callbackUrl = `${window.location.origin}/auth/callback`;
    const params = new URLSearchParams({ callbackUrl, transport: 'cookie' });
    window.location.href = `${this.baseUrl}/auth/saml/login?${params.toString()}`;
  }

  async exchangeCode(code: string): Promise<StoredSession> {
    // The one-time code from the SAML callback is exchanged for session cookies.
    // `credentials: 'include'` is required so the browser stores the Set-Cookie
    // headers from the API response (cross-origin requests block cookies otherwise).
    const response = await fetch(`${this.baseUrl}/auth/saml/token`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });

    if (!response.ok) {
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    // Token response carries only { tokenType, expiresIn } — no user payload.
    // Session cookies are now set; bootstrap the user profile via query { me }.
    const { expiresIn } = (await response.json()) as { expiresIn?: number };
    this.storeTokenTimestamps(expiresIn);
    const user = await this.fetchMe();
    return { user };
  }

  async restoreSession(): Promise<StoredSession | null> {
    try {
      // `cache: 'no-store'` bypasses the HTTP cache so we always get a live
      // cookie validation — a stale 200 from cache would defeat the purpose.
      const response = await fetch(`${this.baseUrl}/auth/saml/validate`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (!response.ok) {
        try {
          sessionStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
          sessionStorage.removeItem(TOKEN_ISSUED_AT_KEY);
        } catch {
          // ignore
        }
        return null;
      }

      // validate returns { valid: true } — session cookie is live.
      // Bootstrap the full user profile via query { me }.
      const user = await this.fetchMe();
      return { user };
    } catch {
      // Network failures (offline, DNS) should degrade gracefully to "no session"
      // rather than crashing the app or showing an error on startup.
      return null;
    }
  }

  async refresh(): Promise<void> {
    // The refresh token is httpOnly too, so there's nothing to pass in the body —
    // the browser sends it automatically via the cookie header.
    const response = await fetch(`${this.baseUrl}/auth/saml/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.status}`);
    }
    const { expiresIn } = (await response.json()) as { expiresIn?: number };
    this.storeTokenTimestamps(expiresIn);
    // Notify other tabs so they update their in-memory expiry state and skip
    // their own proactive refresh when they next gain focus.
    if (expiresIn !== undefined) {
      const now = Date.now();
      this.authChannel?.postMessage({
        type: 'token_refreshed',
        expiresAt: now + expiresIn * 1000,
        issuedAt: now,
      });
    }
  }

  async logout(): Promise<void> {
    // Clear token timestamps immediately — must not survive to restore the
    // session on next page load even if the server-side logout call fails.
    try {
      sessionStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
      sessionStorage.removeItem(TOKEN_ISSUED_AT_KEY);
    } catch {
      // ignore
    }
    try {
      // `global: true` triggers a full SAML Single Logout (SLO), invalidating
      // the session at the IdP level — not just clearing local cookies. The API
      // may return a `logoutUrl` to complete the SLO redirect chain to the IdP.
      // returnTo tells the API where to redirect after the Salesforce SLO
      // round-trip completes. It's passed as SAML RelayState — the IdP echoes
      // it back verbatim. This lets each frontend (providers :5174, members :5173)
      // land on its own login page after logout without hardcoded env vars.
      const response = await fetch(`${this.baseUrl}/auth/saml/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          returnTo: `${window.location.origin}/login`,
        }),
      });
      if (response.ok) {
        const data = (await response.json()) as { logoutUrl?: string };
        if (data.logoutUrl) {
          // Hard-navigate to complete the IdP logout handshake. After the IdP
          // processes SLO it will redirect back to our app (or its own page).
          //
          // IMPORTANT: Return a never-resolving promise instead of void.
          // Setting window.location.href schedules navigation but does NOT
          // stop JS execution — the caller (AuthContext.logout) would proceed
          // to setUser(null), triggering ProtectedRoute → /login → auto SAML
          // login before the browser actually navigates to the SLO URL.
          // The never-resolving promise keeps the caller suspended until the
          // browser navigates away and discards this execution context.
          window.location.href = data.logoutUrl;
          return new Promise(() => {});
        }
      }
    } catch (error) {
      console.error('Logout request failed:', error);
    }
  }

  async clearLocalSession(): Promise<void> {
    try {
      sessionStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
      sessionStorage.removeItem(TOKEN_ISSUED_AT_KEY);
    } catch {
      // ignore
    }
  }

  // Fetches the enriched session profile via query { me } using the current
  // httpOnly session cookie. Called after token exchange and session restore.
  private async fetchMe(): Promise<User> {
    const response = await fetch(`${this.baseUrl}/graphql`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: `query { me {
          id email userType firstName lastName profileName
          providerId practiceId memberId messagingMailboxId
          providerImageUrl sfUsername
        } }`,
      }),
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch user profile: ${response.status}`);
    }
    const data = (await response.json()) as {
      data?: { me?: User };
      errors?: Array<{ message: string }>;
    };
    if (data.errors?.length) throw new Error(data.errors[0].message);
    if (!data.data?.me) throw new Error('No user profile returned from query { me }');
    return data.data.me;
  }

  async getAccessToken(): Promise<string | null> {
    // httpOnly cookies are never accessible to JS — the browser attaches them
    // automatically. There is no token to surface here.
    return null;
  }

  async getTokenExpiresAt(): Promise<number | null> {
    try {
      const raw = sessionStorage.getItem(TOKEN_EXPIRES_AT_KEY);
      if (!raw) return null;
      const value = Number(raw);
      return Number.isFinite(value) ? value : null;
    } catch {
      return null;
    }
  }

  async getTokenIssuedAt(): Promise<number | null> {
    try {
      const raw = sessionStorage.getItem(TOKEN_ISSUED_AT_KEY);
      if (!raw) return null;
      const value = Number(raw);
      return Number.isFinite(value) ? value : null;
    } catch {
      return null;
    }
  }

  // Stores expiresAt and issuedAt in sessionStorage based on the expiresIn
  // value (seconds) from the token or refresh response. No-op if expiresIn
  // is absent or the storage is unavailable.
  private storeTokenTimestamps(expiresIn: number | undefined): void {
    if (expiresIn === undefined) return;
    const now = Date.now();
    try {
      sessionStorage.setItem(TOKEN_EXPIRES_AT_KEY, String(now + expiresIn * 1000));
      sessionStorage.setItem(TOKEN_ISSUED_AT_KEY, String(now));
    } catch {
      // sessionStorage unavailable
    }
  }

  // Single persistent channel instance shared by broadcastLogout() and
  // onCrossTabLogout(). The BroadcastChannel spec guarantees a sender never
  // receives its own messages, so using one object for both send and receive
  // prevents the tab from triggering its own logout handler.
  private _authChannel: BroadcastChannel | null = null;

  private get authChannel(): BroadcastChannel | null {
    if (!this._authChannel) {
      try {
        this._authChannel = new BroadcastChannel(AUTH_CHANNEL);
      } catch {
        // BroadcastChannel unavailable
      }
    }
    return this._authChannel;
  }

  broadcastLogout(): void {
    this.authChannel?.postMessage({ type: 'logout' });
  }

  onCrossTabLogout(handler: () => void): () => void {
    const channel = this.authChannel;
    if (!channel) return () => {};
    channel.onmessage = ({ data }: MessageEvent<{ type: string }>) => {
      if (data.type === 'logout') {
        handler();
      }
    };
    return () => {
      channel.onmessage = null;
    };
  }

  // Web handles the SAML callback via a full browser redirect to /auth/callback —
  // there is no deep link event to listen for.
  onDeepLinkAuth(_handler: (session: StoredSession) => void): () => void {
    return () => {};
  }
}
